import React from 'react';
import { ExternalLink, Check, CloudUpload, AlertTriangle, Pencil } from 'lucide-react';
import { Button } from '@components/ui/button';
import { PageHeader } from '@/components/Page/PageContainer';
import useEditorStore from './editorStore';

/**
 * Top chrome of the canvas editor. The autosave chip is the ONLY feedback
 * channel for content saves (autosave is silent by design — no toasts); the
 * error state stays visible until a later flush succeeds, and unsent edits
 * are requeued by useAutosave, never dropped.
 */

interface Props {
  leagueName: string;
  previewUrl: string;
}

function SaveChip() {
  const saveState = useEditorStore((s) => s.saveState);
  const saveError = useEditorStore((s) => s.saveError);

  if (saveState === 'idle') return null;
  if (saveState === 'error') {
    return (
      <span className="canvas-chip canvas-chip--error" title={saveError || 'Save failed'}>
        <AlertTriangle className="h-3.5 w-3.5" /> Save failed, retrying
      </span>
    );
  }
  if (saveState === 'saving' || saveState === 'dirty') {
    return (
      <span className="canvas-chip canvas-chip--busy">
        <CloudUpload className="h-3.5 w-3.5" /> Saving…
      </span>
    );
  }
  return (
    <span className="canvas-chip canvas-chip--saved">
      <Check className="h-3.5 w-3.5" /> Saved
    </span>
  );
}

export default function CanvasTopBar({ leagueName, previewUrl }: Props) {
  // Standard PageHeader title treatment (owner 2026-09-11) — consistent with
  // the other rail-mode pages (Waivers/Appearance/Settings). Back-to-dashboard
  // lives in the shared AdminRail (owner 2026-09-07). The bar is inherently
  // sticky: the editor is a fixed flex column and only the body scrolls, so
  // the header stays pinned like Appearance's sticky header.
  return (
    <header className="canvas-topbar">
      <PageHeader
        className="flex-1 items-center"
        title={(
          <span className="flex items-center space-x-2">
            <Pencil className="w-5 h-5 text-primary" />
            <span>Site Editor</span>
            <span className="text-sm font-normal text-muted-foreground">• {leagueName}</span>
          </span>
        )}
        description="Edit your public site exactly as visitors see it: posts, pages, and division callouts. Changes save automatically as you type."
        actions={(
          <>
            <SaveChip />
            <Button variant="outline" size="sm" asChild>
              <a href={previewUrl} target="_blank" rel="noreferrer">
                <ExternalLink className="h-4 w-4" /> Preview site
              </a>
            </Button>
          </>
        )}
      />
    </header>
  );
}
